import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import SignalBadge from "@/components/SignalBadge";
import { calcBreakEven, formatRoas, calcSignalClient } from "@/lib/signal";
import { ArrowRight, ChartLine, ShieldCheck, Sparkles, Calculator } from "lucide-react";

const fnum = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v));

export default function Landing() {
  const [sp, setSp] = useState(45);
  const [cogs, setCogs] = useState(12);
  const [other, setOther] = useState(5);
  const [spend, setSpend] = useState(1000);
  const [rev, setRev] = useState(3200);

  const be = useMemo(() => calcBreakEven(fnum(sp), fnum(cogs), fnum(other)), [sp, cogs, other]);
  const roas = useMemo(() => (fnum(spend) > 0 ? fnum(rev) / fnum(spend) : 0), [spend, rev]);
  const buffer = roas - be;
  const signal = calcSignalClient(buffer);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Header />

      {/* Hero */}
      <section className="grain relative overflow-hidden border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold tracking-widest uppercase mb-6">
              <Sparkles className="h-3 w-3" /> For ecom brand owners
            </span>
            <h1 className="font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-[1.05]">
              Should you scale, hold,<br /> or stop?
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              Log your weekly ad performance. SafeScale runs the margin math and gives you a clear
              signal based on your real ROAS buffer — not vibes.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/signup" data-testid="hero-cta-signup">
                <Button className="bg-slate-900 hover:bg-slate-800 h-12 px-6 text-base">
                  Start free — no card <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <a href="#calculator" data-testid="hero-cta-calc">
                <Button variant="outline" className="h-12 px-6 text-base border-slate-300">
                  Try the calculator
                </Button>
              </a>
            </div>
            <div className="mt-8 flex items-center gap-5 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" /> 3-day Pro trial</span>
              <span className="inline-flex items-center gap-1.5"><ChartLine className="h-4 w-4" /> ROAS buffer math</span>
              <span className="inline-flex items-center gap-1.5"><Sparkles className="h-4 w-4" /> AI weekly insight</span>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -top-6 -right-6 h-24 w-24 rounded-full bg-emerald-100 blur-2xl" />
            <Card className="relative bg-white p-6 sm:p-8 shadow-xl rounded-2xl border-slate-200">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">This week</p>
                  <p className="text-sm text-slate-500 mt-1">Synthetic example</p>
                </div>
                <SignalBadge signal="scale" size="lg" />
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <Metric label="ROAS" value="3.20" />
                <Metric label="Break-even" value="1.61" />
                <Metric label="Buffer" value="+1.59" accent="text-emerald-600" />
                <Metric label="Trend" value="Rising" accent="text-emerald-600" />
              </div>
              <div className="h-20 rounded-lg bg-gradient-to-r from-emerald-50 to-emerald-100 grid place-items-center text-emerald-700 text-sm font-medium">
                Healthy margin — push spend +20% this week
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Free Calculator */}
      <section id="calculator" className="py-20 lg:py-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold tracking-widest uppercase mb-4">
              <Calculator className="h-3 w-3" /> Free calculator
            </span>
            <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl text-slate-900">
              See your break-even ROAS in 10 seconds
            </h2>
            <p className="mt-3 text-slate-600">No account required. Real margin math, live.</p>
          </div>

          <Card className="bg-white p-6 sm:p-10 rounded-2xl border-slate-200">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-5">
                <Field id="calc-sp" label="Selling price per unit ($)" value={sp} onChange={setSp} testid="calc-selling-price" />
                <Field id="calc-cogs" label="COGS per unit ($)" value={cogs} onChange={setCogs} testid="calc-cogs" />
                <Field id="calc-other" label="Other costs per order ($)" value={other} onChange={setOther} testid="calc-other-costs" />
                <div className="h-px bg-slate-200 my-2" />
                <Field id="calc-spend" label="Weekly ad spend ($)" value={spend} onChange={setSpend} testid="calc-ad-spend" />
                <Field id="calc-rev" label="Weekly revenue ($)" value={rev} onChange={setRev} testid="calc-revenue" />
              </div>
              <div className="space-y-4">
                <ResultRow label="Break-even ROAS" value={formatRoas(be)} />
                <ResultRow label="Your ROAS" value={formatRoas(roas)} />
                <ResultRow label="Buffer" value={(buffer >= 0 ? "+" : "") + formatRoas(buffer)} accent={buffer >= 0 ? "text-emerald-600" : "text-red-600"} />
                <div className={`p-6 rounded-xl border ${signal === "scale" ? "scale-bg" : signal === "stop" ? "stop-bg" : "hold-bg"}`}>
                  <p className="text-xs font-bold tracking-[0.2em] uppercase opacity-80">Signal</p>
                  <p className="font-display font-bold text-3xl mt-1" data-testid="calc-signal-result">{signal.toUpperCase()}</p>
                </div>
                <Link to="/signup" data-testid="calc-cta-signup">
                  <Button className="w-full h-12 bg-slate-900 hover:bg-slate-800">
                    Track this every week — Start free
                  </Button>
                </Link>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-slate-200 bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-8">
          <FeatCard icon={<ChartLine className="h-5 w-5" />} title="Margin-aware ROAS" body="Break-even ROAS computed from your real product economics — selling price, COGS, ops costs." />
          <FeatCard icon={<ShieldCheck className="h-5 w-5" />} title="3-week trend signal" body="Scale / Hold / Stop based on buffer level and 3-week trend so you stop scaling on noise." />
          <FeatCard icon={<Sparkles className="h-5 w-5" />} title="AI weekly insight" body="Claude reads your recent weeks and writes a sharp 3-sentence action plan." />
        </div>
      </section>

      <footer className="border-t border-slate-200 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between text-sm text-slate-500">
          <p>© {new Date().getFullYear()} SafeScale</p>
          <p>Built for ecom operators.</p>
        </div>
      </footer>
    </div>
  );
}

const Field = ({ id, label, value, onChange, testid }) => (
  <div>
    <Label htmlFor={id} className="text-xs font-bold uppercase tracking-widest text-slate-500">{label}</Label>
    <Input
      id={id}
      type="number"
      step="0.01"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-2 h-11"
      data-testid={testid}
    />
  </div>
);

const ResultRow = ({ label, value, accent = "text-slate-900" }) => (
  <div className="flex items-baseline justify-between border-b border-slate-200 pb-3">
    <span className="text-sm text-slate-500">{label}</span>
    <span className={`font-display font-bold text-2xl ${accent}`}>{value}</span>
  </div>
);

const Metric = ({ label, value, accent = "text-slate-900" }) => (
  <div className="rounded-lg border border-slate-200 p-3">
    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
    <p className={`font-display font-bold text-xl mt-1 ${accent}`}>{value}</p>
  </div>
);

const FeatCard = ({ icon, title, body }) => (
  <div className="lift bg-slate-50 rounded-2xl p-6 border border-slate-200">
    <div className="h-10 w-10 rounded-lg bg-slate-900 text-white grid place-items-center mb-4">{icon}</div>
    <h3 className="font-display font-semibold text-xl text-slate-900">{title}</h3>
    <p className="text-slate-600 mt-2 text-sm leading-relaxed">{body}</p>
  </div>
);
